import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { software } from '@/constants/content'

import {
    clamp,
    mixHex,
    range,
    smoothstep
} from '@/lib/motion/math'

import {
    emptyMetrics,
    enterProgress,
    exitProgress,
    isNear,
    measureSection,
    useEngine
} from '@/lib/motion/section'

import {
    Bracket,
    RevealWords,
    createMaskDriver,
    createStyleCache,
    createWordReveal
} from '@/components/landing/primitives'

const FADING_LETTERS = 4

const SoftwareSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const titleWrapRef = useRef<HTMLDivElement>(null)
    const ruleRef = useRef<HTMLDivElement>(null)
    const dividerRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLParagraphElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const mistRef = useRef<HTMLDivElement>(null)
    const letterRefs = useRef<Array<HTMLSpanElement | null>>([])

    useEngine(() => {
        const set = createStyleCache()
        const mask = createMaskDriver(titleRef.current)
        const reveal = createWordReveal(copyRef.current)
        const letters = software.title.split('')

        let metrics = emptyMetrics()
        let terrainTop = 0
        let copyTop = 0

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current)

                const terrain = document.getElementById('terrain')

                terrainTop = terrain ? terrain.getBoundingClientRect().top + window.scrollY : metrics.top + metrics.height
                copyTop = copyRef.current ? copyRef.current.getBoundingClientRect().top + window.scrollY : metrics.top
            },
            frame: (state) => {
                const vh = state.vh
                const rise = smoothstep(terrainTop - vh * 0.95, terrainTop - vh * 0.15, state.scroll)
                const fall = smoothstep(terrainTop - vh * 0.02, terrainTop + vh * 0.32, state.scroll)
                const mist = clamp(rise * (1 - fall))

                set(mistRef.current, 'opacity', mist.toFixed(3))
                set(mistRef.current, 'visibility', mist > 0.002 ? 'visible' : 'hidden')

                if (!isNear(metrics, state.scroll, vh)) return

                const enter = enterProgress(metrics, state.scroll, vh)
                const exit = exitProgress(metrics, state.scroll, vh)
                const fadeLetters = smoothstep(0.55, 1, enter)

                mask(smoothstep(0.25, 0.95, enter))

                letters.forEach((_, index) => {
                    const fromEnd = letters.length - 1 - index

                    if (fromEnd >= FADING_LETTERS) return

                    const t = clamp(fadeLetters * (FADING_LETTERS + 1.5) - fromEnd)

                    set(letterRefs.current[index], 'color', mixHex('#1d2a31', '#b3bcc0', t))
                })

                set(titleWrapRef.current, 'opacity', (1 - smoothstep(0.1, 0.65, exit)).toFixed(3))
                set(ruleRef.current, 'transform', `scaleX(${smoothstep(0.45, 1, enter).toFixed(4)})`)
                set(dividerRef.current, 'transform', `scaleY(${smoothstep(0.6, 1, enter).toFixed(4)})`)
                set(labelRef.current, 'opacity', smoothstep(0.65, 1, enter).toFixed(3))

                reveal(range(copyTop - vh * 0.88, copyTop - vh * 0.32, state.scroll))
            }
        }
    })

    return (
        <section id='software' ref={sectionRef} aria-labelledby='software-title' className='relative min-h-[150vh] pb-[40vh] pt-[14vh]'>
            <div ref={titleWrapRef} className='relative z-30 px-4 md:px-9'>
                <h2 id='software-title' ref={titleRef} aria-label={software.title} className='display-giant text-[clamp(96px,22vw,400px)] text-ink'>
                    <span className='block overflow-hidden pb-[0.06em]'>
                        <span data-mask-inner='' aria-hidden='true' className='mask-inner block will-change-transform'>
                            {software.title.split('').map((letter, index) => (
                                <span
                                    key={`${letter}-${index}`}
                                    ref={(element) => { letterRefs.current[index] = element }}
                                >
                                    {letter}
                                </span>
                            ))}
                        </span>
                    </span>
                </h2>
            </div>

            <div className='relative mt-10 md:mt-14'>
                <div ref={ruleRef} aria-hidden='true' className='mx-3 h-px origin-left bg-line md:mx-5' />
                <div ref={dividerRef} aria-hidden='true' className='absolute left-1/2 top-0 hidden h-[46vh] w-px origin-top bg-line md:block' />

                <div className='grid gap-10 px-5 pt-8 md:grid-cols-2 md:gap-0 md:px-10 md:pt-12'>
                    <div ref={labelRef} className='flex items-start gap-4 text-haze md:items-end md:pb-2 md:pr-10'>
                        <Bracket>{software.label}</Bracket>
                        <span aria-hidden='true' className='mt-[7px] h-px w-12 bg-pale md:mb-[7px] md:mt-0' />
                    </div>

                    <p ref={copyRef} className='max-w-[620px] text-[clamp(20px,2vw,32px)] font-light leading-[1.25] tracking-[-0.03em] md:pl-10'>
                        <RevealWords text={software.paragraph} />
                    </p>
                </div>
            </div>

            <div
                ref={mistRef}
                aria-hidden='true'
                className='mist-layer pointer-events-none fixed inset-0 z-10'
                style={{ opacity: 0, visibility: 'hidden' }}
            />

            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 bottom-0 h-[40vh] bg-gradient-to-b from-transparent to-mist' />
        </section>
    )
}

export default SoftwareSection
